import React, { useState, useEffect, useRef } from "react";
import "./index.scss";
import Dropdown from "../Dropdown/Dropdown";
import moment from "moment";
import { dropdownItem } from "../Dropdown/types";
import { options } from "./options";
import { timelineItemType, yearArrType, sectionType } from "./types";
import { throttle, getMediaDevice, formatDate } from "../../helpers";
import { loadTheming } from "./theming";
import { themeListener } from "../Theming";

const Module = ({
  stories = [],
  title = "",
  subtitle = "",
  backgroundColor = "",
  headingColor = "",
  textColor = ""
}: {
  stories: timelineItemType[];
  title?: string;
  subtitle?: string;
  backgroundColor?: string;
  headingColor?: string;
  textColor?: string;
}) => {
  const viewportPadding = {
    DESKTOPXL: 425,
    DESKTOP: 400,
    TABLET: 350,
    MOBILE: 400
  };
  const viewportFoldOffset = {
    DESKTOPXL: 230,
    DESKTOP: 230,
    TABLET: 200,
    MOBILE: 250
  };
  const [pageLoad, pageLoaded] = useState(0);
  const [timelinePosition, updatePosition] = useState<{
    current: "above-fold" | "fold" | "below-fold";
    previous: "above-fold" | "fold" | "below-fold" | null;
  }>({
    current: "above-fold",
    previous: null
  });

  const [pageScrollOffset, setPageScrollOffset] = useState(0);
  const [screenWidth, updateScreenWidth] = useState(window.innerWidth);

  const [storyItems, updateStories] = useState<timelineItemType[]>(
    stories
      .map(s => {
        const monthYear =
          typeof s.date == "string" &&
          s.date.split("*").length > 1 &&
          s.date.split("*")[1] == "month";
        const onlyYear =
          typeof s.date == "string" &&
          s.date.split("*").length > 1 &&
          s.date.split("*")[1] == "year";
        return {
          ...s,
          monthYear,
          onlyYear,
          date:
            typeof s.date == "string" && s.date.split("*").length > 1
              ? moment(s.date.split("*")[0])
              : moment(s.date)
        };
      })
      .sort((a, b) => a.date.diff(b.date))
      .map((s, key) => ({ ...s, key, visible: key == 0 }))
  );
  const firstYear =
    (storyItems.length && moment(storyItems[0].date).format("Y")) || 0;
  const [yearValue, updateYear] = useState(firstYear);
  const ModuleWrapper = useRef<HTMLDivElement>(null);
  const Module = useRef<HTMLDivElement | null>(null);
  const [Options, updateOptions] = useState(
    options
      .map((op, key) => ({ ...op, active: key == 0 }))
      .filter(item => {
        const firstDay = moment(`1/1/${item.value}`);
        const lastDay = moment(`12/31/${item.value}`).add(9, "years");

        const thisDecade = storyItems.filter(
          s =>
            moment(s.date).isAfter(firstDay) && moment(s.date).isBefore(lastDay)
        );
        return !!thisDecade.length;
      })
  );

  const decadeDropdownHandler = (item: dropdownItem) => {
    const firstDay = moment(`1/1/${item.value}`);
    const lastDay = moment(`12/31/${item.value}`).add(9, "years");

    const thisDecade = storyItems.filter(
      s => moment(s.date).isAfter(firstDay) && moment(s.date).isBefore(lastDay)
    );
    const ModuleYOffset: number = parseInt(
      `${ModuleWrapper.current &&
        ModuleWrapper.current.getAttribute("yOffset")}`
    );

    const defaultItem: timelineItemType = thisDecade[0];
    const scrollToOffsetValue: number = timeDecadeArr[defaultItem.key].offset;
    const el: null | HTMLDivElement = document.querySelector(
      `.timeline__story--${defaultItem.key}`
    );
    const offsetTop = el && el.offsetTop;

    if (offsetTop != null) {
      window.scrollTo(0, parseInt(`${scrollToOffsetValue + ModuleYOffset}`));
      updateYear(moment(defaultItem.date).format("Y"));
    }
  };

  const [timelineModule, setTimelineModule] = useState<{
    lastStoryHeight: number;
    height: number | null;
    barHeight: number;
  }>({ height: null, barHeight: 0, lastStoryHeight: 0 });
  const [timeDecadeArr, setTimelineDecadeArr] = useState<yearArrType[]>([]);

  const setScrollToValue = () => {
    const timelineKey = Array.from(document.querySelectorAll("main > *"))
      .map((el: HTMLDivElement, key: number | null) => ({
        el,
        timelineKey: el.classList.contains("landing-page") ? key : null
      }))
      .filter(section => section.timelineKey != null)[0].timelineKey;

    const otherOffsets = Array.from(document.querySelectorAll("main > *"))
      .map((el: HTMLDivElement, key: number | null) => ({
        el,
        timelineKey: el.classList.contains("landing-page") ? key : null
      }))
      .reduce(
        (
          totalHeight: number,
          section: { el: HTMLDivElement; timelineKey: number | null },
          key: number
        ) =>
          timelineKey != null && key < timelineKey
            ? totalHeight + section.el.scrollHeight
            : totalHeight,
        0
      );

    const sections = Array.from(
      document.querySelectorAll(".landing-page > div, .landing-page > section")
    ).reduce((obj: sectionType[], s: HTMLElement, key: number) => {
      obj.push({
        key,
        height: s.offsetHeight,
        isTimeline: !!s.querySelectorAll(".timeline").length,
        class: s.className,
        scrollTop: obj.reduce(
          (total: number, item: sectionType) => total + item.height,
          0
        )
      });
      if (!!s.querySelectorAll(".timeline").length) {
        const stories: HTMLDivElement[] | null = Array.from(
          s.querySelectorAll(".timeline__story")
        );
        const height: number =
          stories &&
          stories.reduce((total: number, el: HTMLDivElement) => {
            return total + el.scrollHeight;
          }, 0);
        const lastStoryHeight: number =
          stories[stories.length - 1].offsetHeight - 90;
        setTimelineModule({
          lastStoryHeight,
          barHeight: height - lastStoryHeight,
          height: height + viewportFoldOffset[getMediaDevice()]
        });
      }
      return obj;
    }, []);
    const timelineEl = sections.filter(s => s.isTimeline)[0];
    const scrollTop: number | undefined =
      (timelineEl.scrollTop || 0) + otherOffsets;
    ModuleWrapper.current &&
      ModuleWrapper.current.setAttribute("yOffset", `${scrollTop}`);

    const yearArr = storyItems.reduce(
      (arr: yearArrType[], s: timelineItemType) => {
        const el: HTMLDivElement | null = document.querySelector(
          `.timeline__story--${s.key}`
        );
        arr.push({
          offset: arr.reduce(
            (total: number, item: yearArrType) => total + item.height,
            0
          ),
          decade: `${moment(s.date)
            .format("Y")
            .slice(0, 3)}0`,
          key: s.key,
          year: moment(s.date).format("Y"),
          height: (el && el.offsetHeight) || 0
        });
        return arr;
      },
      []
    );

    setTimelineDecadeArr(yearArr);

    return {
      scrollTop
    };
  };

  // fired only when page is scrolling and screen-resizing: "scroll" | "resize" event handler
  useEffect(() => {
    const htmlEl = document.querySelector("html");

    if (Module.current && ModuleWrapper.current && htmlEl) {
      const ModuleYOffset: number = parseInt(
        `${ModuleWrapper.current.getAttribute("yOffset")}`
      );

      const value =
        window.scrollY - ModuleYOffset < 0 ? 0 : window.scrollY - ModuleYOffset;
      const index = (padding: boolean = false) =>
        timeDecadeArr.findIndex(
          y =>
            value + (padding ? viewportPadding[getMediaDevice()] : 20) <
            y.offset
        );

      const yearKey: number =
        index() <= timeDecadeArr.length && index() > 0
          ? index() - 1
          : index() == -1 && timeDecadeArr.length != 0
          ? timeDecadeArr.length - 1
          : 0;

      const nextKey: number =
        index(true) <= timeDecadeArr.length && index(true) > 0
          ? index(true) - 1
          : index(true) == -1 && timeDecadeArr.length != 0
          ? timeDecadeArr.length - 1
          : 0;

      const newStories = storyItems.map((s, key) => ({
        ...s,
        visible: key <= nextKey
      }));

      const thisDecade = `${moment(storyItems[nextKey].date)
        .format("Y")
        .slice(0, 3)}0`;

      const timeLineEndYScroll =
        ModuleYOffset +
        ((timelineModule.height || 0) + 100) -
        window.innerHeight -
        timelineModule.lastStoryHeight;
      const isTimelineVisible =
        window.pageYOffset < timeLineEndYScroll &&
        window.pageYOffset > ModuleYOffset;
      const isTimelineNotVisible =
        Math.floor(window.pageYOffset) < ModuleYOffset;

      const timelineProgressBarCSS = `
        @property --progressBarWidth {
          syntax: "<length>";
          inherits: false;
          initial-value: ${timelineModule.barHeight - 200}px;
        }

        @keyframes progressbar-expand {
          0% { --progressBarWidth: ${timelineModule.barHeight - 200}px; }
          100% { --progressBarWidth: ${timelineModule.barHeight}px; }
        }

        @keyframes progressbar-shrink {
          0% { --progressBarWidth: ${timelineModule.barHeight}px; }
          100% { --progressBarWidth: 0; }
        }
      `;

      if (timelinePosition.current == "below-fold") {
        const headEl = document.querySelector("head");
        const progressStyleNode = document.querySelector("#progress-bar");
        const styleNode = progressStyleNode
          ? progressStyleNode
          : document.createElement("style");
        styleNode.id = "progress-bar";
        styleNode.innerHTML = timelineProgressBarCSS;
        if (headEl && !progressStyleNode) {
          headEl.appendChild(styleNode);
        }
      }

      updateOptions(
        Options.map(op => ({
          ...op,
          active: `${op.label.slice(0, 3)}0` == thisDecade
        }))
      );
      updateStories([...newStories]);
      updateYear(moment(storyItems[yearKey].date).format("Y"));
      // assign module state: with the page fold
      if (isTimelineNotVisible) {
        updatePosition({ ...timelinePosition, current: "above-fold" });
      } else if (isTimelineVisible) {
        updatePosition({
          ...timelinePosition,
          current: "fold",
          previous: timelinePosition.current
        });
      } else if (window.pageYOffset >= timeLineEndYScroll) {
        updatePosition({
          ...timelinePosition,
          current: "below-fold",
          previous: timelinePosition.current
        });
      }
    }
  }, [pageScrollOffset, screenWidth, pageLoad]);

  useEffect(() => {
    pageLoaded(pageLoad + 1);
    themeListener({
      callback: () => {
        pageLoaded(pageLoad + 1);
      },
      name: "olympics"
    });
    loadTheming({
      backgroundColor,
      headingColor,
      textColor
    });
    throttle({
      delay: 5,
      event: "resize",
      func: () => {
        setScrollToValue();
        updateScreenWidth(window.innerWidth);
        setPageScrollOffset(window.pageYOffset);
      }
    });

    throttle({
      delay: 5,
      event: "scroll",
      func: () => {
        setScrollToValue();
        setPageScrollOffset(window.pageYOffset);
      }
    });
  }, []);

  return (
    <div
      className={`timeline-wrapper ${timelinePosition.current} ${
        !timelineModule.height ? " hide" : ""
      }`}
    >
      <div
        className={`timeline ${timelinePosition.current}`}
        ref={ModuleWrapper}
      >
        <div className="page-section">
          <div
            className="page-section__inner timeline-page-section"
            style={{ height: `${timelineModule.height}px` }}
          >
            <div className="sticky-container progress-bar"></div>
            <div className="sticky-container">
              <div className="timeline__heading">
                <h1>
                  {title.length ? title : null}
                  {subtitle && subtitle != null ? <p>{subtitle}</p> : null}
                </h1>
                <div className="timeline__dropdown">
                  <Dropdown
                    placeholder="Select Decade"
                    value={Options.filter(op => op.active)[0]}
                    onChange={decadeDropdownHandler}
                    options={Options}
                  />
                </div>
              </div>
              <div className="timeline__year">
                <div className="timeline__year-value">{yearValue}</div>
              </div>
            </div>
            <div className="timeline__container">
              <div className="timeline__stories" ref={Module}>
                {storyItems.map((story, key) => {
                  return (
                    <div
                      key={key}
                      className={`timeline__story timeline__story--${key} ${
                        story.visible ? "show" : "hide"
                      }${storyItems.length - 1 == key ? " last" : ""}`}
                    >
                      <div className="timeline__story-wrapper">
                        {story.date ? (
                          <div className="timeline__story-date">
                            {story.monthYear
                              ? formatDate(moment(story.date)).longtNoDay
                              : story.onlyYear
                              ? formatDate(moment(story.date)).rawYear
                              : formatDate(moment(story.date)).longtNoComma}
                          </div>
                        ) : null}
                        {story.title.length ? (
                          <h3 className="timeline__story-title">
                            {story.title}
                          </h3>
                        ) : null}
                        {story.subtitle.length ? (
                          <p className="timeline__story-subtitle">
                            {story.subtitle}
                          </p>
                        ) : null}
                        {story.imageUrl ? (
                          <div className="timeline__story-image">
                            <img src={story.imageUrl} />
                          </div>
                        ) : null}
                        {story.blurb.length ? (
                          <div className="timeline__story-text">
                            <p>{story.blurb}</p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Module;
